/**
 * EQ / headroom math adapted from Cadence (MIT) — RBJ Audio EQ Cookbook
 * formulas matching Web Audio BiquadFilterNode.
 * https://github.com/DRNZY/Cadence
 */

export const EQ_FREQUENCIES = [
  32, 64, 125, 250, 500, 1000, 2000, 4000, 8000, 16000,
] as const;

export const EQ_DEFAULT_Q = 1.414;

export type EqBandType = 'lowshelf' | 'peaking' | 'highshelf';

export function eqBandType(index: number): EqBandType {
  if (index === 0) return 'lowshelf';
  if (index === EQ_FREQUENCIES.length - 1) return 'highshelf';
  return 'peaking';
}

const SHELF_SLOPE = 1;

/** Normalised biquad coefficients: [b0, b1, b2, a1, a2]. */
export function eqBandCoefficients(
  type: EqBandType,
  freqHz: number,
  gainDb: number,
  q: number,
  sampleRate: number,
): Float64Array {
  const nyquist = sampleRate * 0.5;
  const f0 = Math.min(Math.max(freqHz, 1e-3), nyquist * 0.999);
  const A = Math.pow(10, gainDb / 40);
  const w0 = (2 * Math.PI * f0) / sampleRate;
  const cosW0 = Math.cos(w0);
  const sinW0 = Math.sin(w0);

  let b0: number;
  let b1: number;
  let b2: number;
  let a0: number;
  let a1: number;
  let a2: number;

  if (type === 'peaking') {
    const alpha = sinW0 / (2 * Math.max(q, 1e-4));
    b0 = 1 + alpha * A;
    b1 = -2 * cosW0;
    b2 = 1 - alpha * A;
    a0 = 1 + alpha / A;
    a1 = -2 * cosW0;
    a2 = 1 - alpha / A;
  } else {
    const sq = Math.sqrt((A + 1 / A) * (1 / SHELF_SLOPE - 1) + 2);
    const alpha = (sinW0 / 2) * sq;
    const twoSqrtAAlpha = 2 * Math.sqrt(A) * alpha;
    if (type === 'lowshelf') {
      b0 = A * (A + 1 - (A - 1) * cosW0 + twoSqrtAAlpha);
      b1 = 2 * A * (A - 1 - (A + 1) * cosW0);
      b2 = A * (A + 1 - (A - 1) * cosW0 - twoSqrtAAlpha);
      a0 = A + 1 + (A - 1) * cosW0 + twoSqrtAAlpha;
      a1 = -2 * (A - 1 + (A + 1) * cosW0);
      a2 = A + 1 + (A - 1) * cosW0 - twoSqrtAAlpha;
    } else {
      b0 = A * (A + 1 + (A - 1) * cosW0 + twoSqrtAAlpha);
      b1 = -2 * A * (A - 1 + (A + 1) * cosW0);
      b2 = A * (A + 1 + (A - 1) * cosW0 - twoSqrtAAlpha);
      a0 = A + 1 - (A - 1) * cosW0 + twoSqrtAAlpha;
      a1 = 2 * (A - 1 - (A + 1) * cosW0);
      a2 = A + 1 - (A - 1) * cosW0 - twoSqrtAAlpha;
    }
  }

  if (!Number.isFinite(a0) || Math.abs(a0) < 1e-12) a0 = 1;
  return new Float64Array([b0 / a0, b1 / a0, b2 / a0, a1 / a0, a2 / a0]);
}

function bandMagnitude(
  c: Float64Array,
  cos1: number,
  sin1: number,
  cos2: number,
  sin2: number,
): number {
  const numRe = c[0]! + c[1]! * cos1 + c[2]! * cos2;
  const numIm = -(c[1]! * sin1 + c[2]! * sin2);
  const denRe = 1 + c[3]! * cos1 + c[4]! * cos2;
  const denIm = -(c[3]! * sin1 + c[4]! * sin2);
  const den = Math.hypot(denRe, denIm);
  return den > 1e-30 ? Math.hypot(numRe, numIm) / den : 0;
}

const GRID_POINTS = 512;
const GRID_MIN_HZ = 3;
const GRID_CACHE = new Map<
  number,
  { cos1: Float64Array; sin1: Float64Array; cos2: Float64Array; sin2: Float64Array }
>();

function getGrid(sampleRate: number) {
  const cached = GRID_CACHE.get(sampleRate);
  if (cached) return cached;
  const cos1 = new Float64Array(GRID_POINTS);
  const sin1 = new Float64Array(GRID_POINTS);
  const cos2 = new Float64Array(GRID_POINTS);
  const sin2 = new Float64Array(GRID_POINTS);
  for (let i = 0; i < GRID_POINTS; i++) {
    const f =
      GRID_MIN_HZ *
      Math.pow((sampleRate * 0.5) / GRID_MIN_HZ, i / (GRID_POINTS - 1));
    const w = (2 * Math.PI * f) / sampleRate;
    cos1[i] = Math.cos(w);
    sin1[i] = Math.sin(w);
    cos2[i] = Math.cos(2 * w);
    sin2[i] = Math.sin(2 * w);
  }
  const grid = { cos1, sin1, cos2, sin2 };
  GRID_CACHE.set(sampleRate, grid);
  return grid;
}

export function eqPeakBoostDb(
  gains: ArrayLike<number>,
  sampleRate: number,
  qs?: ArrayLike<number>,
): number {
  const bands = EQ_FREQUENCIES.length;
  if (!(sampleRate > 0)) return 0;
  const coeff: (Float64Array | null)[] = new Array(bands);
  for (let i = 0; i < bands; i++) {
    const g = typeof gains[i] === 'number' && Number.isFinite(gains[i]) ? gains[i]! : 0;
    if (g === 0) {
      coeff[i] = null;
      continue;
    }
    const q =
      qs && typeof qs[i] === 'number' && qs[i]! > 0 ? qs[i]! : EQ_DEFAULT_Q;
    coeff[i] = eqBandCoefficients(
      eqBandType(i),
      EQ_FREQUENCIES[i]!,
      g,
      q,
      sampleRate,
    );
  }
  const grid = getGrid(sampleRate);
  let peakLin = 1;
  for (let p = 0; p < GRID_POINTS; p++) {
    let mag = 1;
    for (let i = 0; i < bands; i++) {
      const c = coeff[i];
      if (!c) continue;
      mag *= bandMagnitude(
        c,
        grid.cos1[p]!,
        grid.sin1[p]!,
        grid.cos2[p]!,
        grid.sin2[p]!,
      );
    }
    if (mag > peakLin) peakLin = mag;
  }
  return 20 * Math.log10(peakLin);
}

/** Negative trim so EQ boosts cannot clip; never raises an all-cut curve. */
export function eqHeadroomDb(gains: ArrayLike<number>, sampleRate: number): number {
  const peak = eqPeakBoostDb(gains, sampleRate);
  return peak > 0 ? -peak : 0;
}

/** Sample response curve (dB) across log frequency for graph drawing. */
export function eqResponseCurveDb(
  gains: ArrayLike<number>,
  sampleRate: number,
  points = 128,
): { freq: number; db: number }[] {
  const out: { freq: number; db: number }[] = [];
  const bands = EQ_FREQUENCIES.length;
  const coeff: (Float64Array | null)[] = new Array(bands);
  for (let i = 0; i < bands; i++) {
    const g = typeof gains[i] === 'number' && Number.isFinite(gains[i]) ? gains[i]! : 0;
    if (g === 0) {
      coeff[i] = null;
      continue;
    }
    coeff[i] = eqBandCoefficients(
      eqBandType(i),
      EQ_FREQUENCIES[i]!,
      g,
      EQ_DEFAULT_Q,
      sampleRate,
    );
  }
  const fMin = 20;
  const fMax = Math.min(20000, sampleRate * 0.49);
  for (let i = 0; i < points; i++) {
    const freq = fMin * Math.pow(fMax / fMin, i / (points - 1));
    const w = (2 * Math.PI * freq) / sampleRate;
    const cos1 = Math.cos(w);
    const sin1 = Math.sin(w);
    const cos2 = Math.cos(2 * w);
    const sin2 = Math.sin(2 * w);
    let mag = 1;
    for (let b = 0; b < bands; b++) {
      const c = coeff[b];
      if (!c) continue;
      mag *= bandMagnitude(c, cos1, sin1, cos2, sin2);
    }
    out.push({ freq, db: 20 * Math.log10(Math.max(mag, 1e-8)) });
  }
  return out;
}

/** Preset gains keyed alphabetically (UI lists Object.keys in insertion order). */
export const EQ_PRESETS: Record<string, number[]> = {
  Acoustic: [0, 1, 2, 3, 2, 1, 1, 0, 0, 0],
  'Bass Boost': [6, 4, 2, 0, 0, 0, 0, 0, 0, 0],
  Electronic: [4, 5, 2, 0, -1, 1, 3, 5, 5, 4],
  Flat: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  Soft: [2, 1, 0, 0, 0, 0, -1, -2, -2, -1],
  Treble: [0, 0, 0, 0, 0, 1, 2, 4, 5, 4],
  Vocal: [-2, -1, 0, 2, 4, 3, 1, 0, -1, -2],
};
